import { useState } from 'react';
import {
    mdiChevronRight,
    mdiEyeOutline,
    mdiMinus,
    mdiPlus,
    mdiSchool,
} from '@mdi/js';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';

import {
    IPortfolioItemProgressFragment,
    IScormListItemFragment,
} from 'graphql/types';
import { Icon } from 'common/components/Icon';
import { IconButton } from 'common/components/IconButton';
import {
    ListItem,
    ListItemActionText,
    ListItemMedia,
    ListItemSecondaryAction,
    ListItemText,
    ListItemFooter,
    ListItemButton,
} from 'common/components/ListItem';
import { Tooltip } from 'common/components/Tooltip';
import { ScormPreviewModal } from 'training/components/ScormPreviewModal';
import { PortfolioItemProgressScoreChip } from 'user/components/PortfolioItemProgressScoreChip';

interface IProps extends React.ComponentProps<typeof ListItem> {
    actionText?: string;
    addButton?: boolean;
    added?: boolean;
    disabled?: boolean;
    loading?: boolean;
    portfolioItem?: IPortfolioItemProgressFragment | null;
    selected?: boolean;
    scorm: IScormListItemFragment;
    showActions?: boolean;
    footer?: React.ReactNode;
    onClick?(scorm: IScormListItemFragment): void;
}

export const ScormListItem = ({
    actionText,
    addButton,
    added,
    disabled,
    loading,
    portfolioItem,
    selected,
    scorm,
    onClick,
    showActions,
    footer,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const [showPreview, setShowPreview] = useState(false);

    const { id, title, thumbnail } = scorm;

    const previewAction = showActions && (
        <Tooltip title={translate('preview')}>
            <IconButton
                aria-label={translate('preview')}
                color="inherit"
                iconPath={mdiEyeOutline}
                iconSize="2.4rem"
                onClick={() => setShowPreview(true)}
            />
        </Tooltip>
    );

    const showActionText =
        !showActions &&
        !addButton &&
        !disabled &&
        ((loading && portfolioItem) || (!loading && !portfolioItem));

    let mediaIconPath = mdiSchool;

    if (addButton) {
        mediaIconPath = selected || added ? mdiMinus : mdiPlus;
    }

    const baseContent = (
        <>
            <ListItemMedia
                color="primary"
                image={thumbnail?.url || undefined}
                size="small"
            >
                <Icon path={mediaIconPath} size="2.2rem" sizeSm="3rem" />
            </ListItemMedia>

            <ListItemText primary={title} />

            {showActionText && (
                <>
                    {!loading && (
                        <ListItemActionText>
                            {actionText || translate('open')}
                        </ListItemActionText>
                    )}
                    <ListItemSecondaryAction>
                        {loading ? (
                            <CircularProgress color="primary" size={30} />
                        ) : (
                            <Icon path={mdiChevronRight} size="3rem" />
                        )}
                    </ListItemSecondaryAction>
                </>
            )}
        </>
    );

    return (
        <>
            <ListItem
                button={!disabled}
                disableHover={disabled}
                key={id}
                px={0}
                selected={selected}
                tabIndex={-1}
                {...other}
            >
                {disabled ? (
                    baseContent
                ) : (
                    <ListItemButton
                        onClick={() => !disabled && onClick?.(scorm)}
                    >
                        {baseContent}
                    </ListItemButton>
                )}

                {previewAction && (
                    <ListItemSecondaryAction hideXs>
                        {previewAction}
                    </ListItemSecondaryAction>
                )}

                {!loading && !!portfolioItem && (
                    <PortfolioItemProgressScoreChip
                        portfolioItem={portfolioItem}
                    />
                )}

                {footer && <ListItemFooter>{footer}</ListItemFooter>}
            </ListItem>

            {showActions && (
                <ScormPreviewModal
                    open={showPreview}
                    scormId={id}
                    title={title}
                    onClose={() => setShowPreview(false)}
                />
            )}
        </>
    );
};
